import { useContext, useEffect, useMemo } from "react"
import { useSortedModels, type Model } from "resources/models"
import { usePortfolios, type Portfolio } from "resources/portfolios"
import { combineStatuses, OK } from "helpers/data-statuses"
import { getMostUsedOrDefaultModelId } from "helpers/models"
import { IDashboardContext } from "connected-components/dashboard/context/types"
import ModelSelectorAutocomplete from "connected-components/model-selector-autocomplete"
import { useModelId } from "connected-components/dashboard/hooks/use-model-id"

export default function ModelSelector({ context, disabled = false, onModelChange }: ModelSelectorProps) {
  const { setModelId } = useContext(context)
  const modelId = useModelId()
  const [portfolios, portfoliosStatus] = usePortfolios()
  const [models, modelsStatus] = useSortedModels()

  const status = useMemo(
    () => combineStatuses([modelsStatus, portfoliosStatus]),
    [modelsStatus, portfoliosStatus],
  )

  useEffect(
    () => {
      if (modelsStatus === OK && portfoliosStatus === OK && !modelId) {
        const defaultModelId = getMostUsedOrDefaultModelId(portfolios as Portfolio[], models as Model[])
        if (defaultModelId) {
          setModelId(defaultModelId)
        }
      }
    },
    [modelId, setModelId, portfolios, models, modelsStatus, portfoliosStatus],
  )

  const handleModelChange = onModelChange || setModelId

  if (status !== OK || !models) {
    return <div>Loading</div>
  }

  return <ModelSelectorAutocomplete
    models={models}
    value={modelId || ""}
    onChange={handleModelChange}
    disabled={disabled}
    popperPlacement="bottom-end"
  />
}

interface ModelSelectorProps {
  context: IDashboardContext
  disabled?: boolean
  onModelChange?: (modelId: string) => void
}
